/**
 * Live-run display helpers for the Integrations surfaces. Centralised so every
 * integration's runs list (the section shell + the status pages, Confluence and
 * the future Skimmer / Register tabs included) polls + ticks the same way -
 * deliberately without the heavy sync_operations / WebSocket machinery.
 */
import { useEffect, useRef, useState } from 'react'

import type { IntegrationRun } from '../../../../../services/admin/integrationsApi'

/** True while any run in the list is still `running`. */
export const anyRunActive = (runs?: IntegrationRun[] | null): boolean =>
  !!runs?.some((r) => r.status === 'running')

/**
 * Re-fetch on an interval while any run is still `running`, then stop once none
 * are. Borrows the FP-sync "poll while active, idle otherwise" idea. `refetch`
 * fires every `intervalMs` until the runs settle; held in a ref so a changing
 * callback doesn't re-arm the timer each render.
 */
export const usePollWhileRunning = (
  runs: IntegrationRun[] | null | undefined,
  refetch: () => void,
  intervalMs = 2500,
): void => {
  const active = anyRunActive(runs)
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => refetchRef.current(), intervalMs)
    return () => window.clearInterval(id)
  }, [active, intervalMs])
}

/**
 * A clock that ticks every `intervalMs` (default 1s) **only while** `enabled`,
 * so a running row can show a live elapsed timer without re-fetching. Returns
 * `Date.now()` and re-renders the consumer on each tick; freezes when disabled.
 */
export const useNow = (enabled: boolean, intervalMs = 1000): number => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!enabled) return
    setNow(Date.now())
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [enabled, intervalMs])
  return now
}
